import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
// material
import { styled, useTheme } from '@mui/material/styles';
import {
  Card, Stack, Typography, FormControl, InputLabel, Select, MenuItem, Button, CircularProgress, Box,
} from '@mui/material';
// components
import Page from '../components/Page';
import { APIGetOperator } from '../utils/apiCalled';
import { getFirstEnabledModule, usePrivilege } from '../PrivilegeContext';

// ----------------------------------------------------------------------

const RootStyle = styled(Page)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100vh',
  backgroundColor: '#ffffff',
}));

const CardStyle = styled(Card)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  maxWidth: 500,
  width: '100%',
  margin: theme.spacing(2),
}));

// ----------------------------------------------------------------------

export default function OperatorSelect() {
  const navigate = useNavigate();
  const theme = useTheme();
  const { modules } = usePrivilege();
  const [operators, setOperators] = useState([]);
  const [assignedOperators, setAssignedOperators] = useState([]);
  const [selectedOperatorId, setSelectedOperatorId] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Parse operatorAssigned cookie and fetch operators
  useEffect(() => {
    const fetchOperators = async () => {
      try {
        setLoading(true);

        // Get operatorAssigned from cookie
        const operatorAssignedCookie = Cookies.get('operatorAssigned');

        if (!operatorAssignedCookie) {
          setError('No operators assigned to your account. Please contact your administrator.');
          setLoading(false);
          return;
        }

        // Parse operatorAssigned - could be JSON string, array string, or comma-separated string
        let assignedOperatorIds = [];
        try {
          // Try parsing as JSON first
          const parsed = JSON.parse(operatorAssignedCookie);
          if (Array.isArray(parsed)) {
            // If it's an array of objects, extract IDs
            assignedOperatorIds = parsed.map((op) => {
              if (typeof op === 'object' && op._id) {
                return op._id.toString();
              }
              return op.toString();
            });
          } else if (typeof parsed === 'string') {
            // If it's a string, try splitting by comma
            assignedOperatorIds = parsed.split(',').map((id) => id.trim());
          }
        } catch (e) {
          // If JSON parsing fails, treat as comma-separated string
          if (typeof operatorAssignedCookie === 'string') {
            assignedOperatorIds = operatorAssignedCookie.split(',').map((id) => id.trim());
          }
        }

        if (assignedOperatorIds.length === 0) {
          setError('No operators assigned to your account. Please contact your administrator.');
          setLoading(false);
          return;
        }

        // Fetch all operators
        const response = await APIGetOperator(setLoading);

        if (response.status === 200 && Array.isArray(response.data)) {
          // Filter operators to only show assigned ones
          const filteredOperators = response.data.filter((operator) => (
            assignedOperatorIds.includes(operator._id.toString())
          ));

          if (filteredOperators.length === 0) {
            setError('No valid operators found. Please contact your administrator.');
            setLoading(false);
            return;
          }

          setAssignedOperators(filteredOperators);

          // If only one operator, auto-select it
          if (filteredOperators.length === 1) {
            const singleOperator = filteredOperators[0];
            setSelectedOperatorId(singleOperator._id);
            // Auto-navigate after a short delay to ensure state is updated
            // Note: assignedOperators will be set before this runs,
            // so handleOperatorSelect can access it
            setTimeout(() => {
              // Store operator name directly since we have the operator object
              const operatorName = singleOperator.operatorName
                || singleOperator.operatorID
                || singleOperator._id;
              Cookies.set('selectedOperatorId', singleOperator._id);
              if (operatorName) {
                Cookies.set('selectedOperatorName', operatorName);
              }

              // Navigate to dashboard
              const firstModule = getFirstEnabledModule(modules);
              if (firstModule && firstModule.key) {
                navigate(`/dashboard/${firstModule.key}`, { replace: true });
              } else {
                navigate('/dashboard', { replace: true });
              }
            }, 100);
          }
        } else {
          setError('Failed to fetch operators. Please try again.');
        }
      } catch (err) {
        console.error('Error fetching operators:', err);
        setError('An error occurred while fetching operators. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchOperators();
  }, []);

  const handleOperatorSelect = async (operatorId, autoNavigate = false) => {
    if (!operatorId) return;

    try {
      setSubmitting(true);

      // Find the selected operator to get its name
      const selectedOperator = assignedOperators.find((op) => op._id === operatorId);
      const operatorName = selectedOperator
        ? (selectedOperator.operatorName || selectedOperator.operatorID || selectedOperator._id)
        : '';

      // Save selected operator ID and name to cookies
      Cookies.set('selectedOperatorId', operatorId);
      if (operatorName) {
        Cookies.set('selectedOperatorName', operatorName);
      }

      // Get the first enabled module
      const firstModule = getFirstEnabledModule(modules);

      if (firstModule && firstModule.key) {
        // Navigate to dashboard with first enabled module
        navigate(`/dashboard/${firstModule.key}`, { replace: true });
      } else {
        // Fallback to dashboard root
        navigate('/dashboard', { replace: true });
      }
    } catch (err) {
      console.error('Error selecting operator:', err);
      setError('An error occurred while selecting operator. Please try again.');
      setSubmitting(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (selectedOperatorId) {
      handleOperatorSelect(selectedOperatorId);
    }
  };

  if (loading) {
    return (
      <RootStyle title="Select Operator">
        <CardStyle>
          <Stack sx={{ alignItems: 'center', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
            <Typography sx={{ mt: 3, color: theme.palette.grey[600] }}>
              Loading operators...
            </Typography>
          </Stack>
        </CardStyle>
      </RootStyle>
    );
  }

  if (error) {
    return (
      <RootStyle title="Select Operator">
        <CardStyle>
          <Stack spacing={3}>
            <Typography variant="h5" sx={{ fontWeight: 600, color: theme.palette.grey[900] }}>
              Select Operator
            </Typography>
            <Typography sx={{ color: theme.palette.error.main }}>
              {error}
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate('/login')}
              sx={{ alignSelf: 'flex-start' }}
            >
              Back to Login
            </Button>
          </Stack>
        </CardStyle>
      </RootStyle>
    );
  }

  return (
    <RootStyle title="Select Operator">
      <CardStyle>
        <Stack spacing={3}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: theme.palette.grey[900] }}>
            Select Operator
          </Typography>
          <Typography sx={{ color: theme.palette.grey[600], fontSize: '0.95rem' }}>
            Choose which operator context you want to work in.
          </Typography>
          <Box component="form" onSubmit={handleSubmit}>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="operator-select-label">Operator</InputLabel>
              <Select
                labelId="operator-select-label"
                id="operator-select"
                value={selectedOperatorId}
                label="Operator"
                onChange={(e) => setSelectedOperatorId(e.target.value)}
                required
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.grey[300],
                  },
                }}
              >
                {assignedOperators.map((operator) => (
                  <MenuItem key={operator._id} value={operator._id}>
                    {operator.operatorName || operator.operatorID || operator._id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                type="submit"
                variant="contained"
                disabled={!selectedOperatorId || submitting}
                onClick={handleSubmit}
                sx={{
                  px: 3,
                  py: 1,
                  borderRadius: 1,
                  bgcolor: selectedOperatorId && !submitting
                    ? theme.palette.primary.main
                    : theme.palette.grey[300],
                  color: selectedOperatorId && !submitting ? 'white' : theme.palette.grey[500],
                  '&:hover': {
                    bgcolor: selectedOperatorId && !submitting
                      ? theme.palette.primary.dark
                      : theme.palette.grey[300],
                  },
                  '&.Mui-disabled': {
                    bgcolor: theme.palette.grey[300],
                    color: theme.palette.grey[500],
                  },
                }}
              >
                {submitting ? <CircularProgress size={20} /> : 'Confirm'}
              </Button>
            </Box>
          </Box>
        </Stack>
      </CardStyle>
    </RootStyle>
  );
}
